/**
 * O cartão que abre o formulário (o `WizardAmbiente`) do rascunho de ambiente
 * novo assim que ele nasce — consoleai#25762.
 *
 * Antes, o wizard só abria pelo cartão da criação (`terraform_stage_create`),
 * que só sai com o rascunho completo; até lá era pergunta e resposta no chat
 * (conversa 851879: sete itens numerados respondidos à mão). Este cartão sai na
 * primeira anotação (`abrir_formulario_de_ambiente`, posto pelo servidor).
 *
 * CONFIRMA QUANDO O RASCUNHO FICA COMPLETO (consoleai#26021, conversa 875358):
 * o servidor posta o cartão da criação e o formulário devolve
 * `cartao_de_criacao` com o `pode_confirmar` de quem vê — os mesmos portões do
 * botão do cartão. Com ele, o wizard ganha `onConfirmar`, que chama o MESMO
 * confirm da mensagem do cartão (`useConfirmAction`): um caminho de confirmação
 * só. Sem ele, a Revisão diz o motivo. Quem não enxerga o tenant do rascunho
 * recebe `tenantId` nulo do servidor e nem abre o formulário.
 */
import { useState } from 'react'
import { Button, Card, Group, Text } from '@mantine/core'
import { IconForms } from '@tabler/icons-react'
import { useConfirmAction } from '@/hooks/useChat'
import type { CartaoDeCriacao } from '@/api/rascunhoDeAmbiente'
import { WizardAmbiente } from '../wizard-ambiente/WizardAmbiente'

interface Props {
  conversationId: number
  rascunhoId: number
  /** `null` para quem não enxerga o tenant do rascunho (ou réplica antiga). */
  tenantId: number | null
  projeto?: string
  ambiente?: string
  /** O passo em que o formulário abre (consoleai#25981). */
  passo?: string
}

export function FormularioDoAmbienteCard({
  conversationId, rascunhoId, tenantId, projeto, ambiente, passo,
}: Props) {
  const [aberto, setAberto] = useState(false)
  const [cartao, setCartao] = useState<CartaoDeCriacao | null>(null)
  const confirmMutation = useConfirmAction(conversationId)
  const podeAbrir = tenantId !== null && Number.isFinite(rascunhoId)
  const podeConfirmar = cartao?.pode_confirmar === true
  return (
    <Card withBorder padding="sm" radius="md" style={{ maxWidth: 480 }}>
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconForms size={18} stroke={1.5} aria-hidden />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {`Formulário do ambiente ${projeto ?? '?'}/${ambiente ?? '?'} — rascunho #${rascunhoId}`}
        </Text>
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        Preencha e confira campo a campo; a conversa segue para dúvidas. Quando o rascunho estiver
        completo, o Confirmar aparece na Revisão.
      </Text>
      {podeAbrir ? (
        <Button size="xs" variant="default" leftSection={<IconForms size={14} stroke={1.5} />}
                onClick={() => setAberto(true)}>
          Abrir o formulário
        </Button>
      ) : (
        <Text size="xs" c="dimmed" ff="JetBrains Mono, monospace">Tenant do rascunho: sem acesso</Text>
      )}
      {podeAbrir && (
        <WizardAmbiente aberto={aberto} onFechar={() => setAberto(false)}
                        tenantId={tenantId} rascunhoId={rascunhoId}
                        podeConfirmar={podeConfirmar}
                        motivoBloqueio={cartao?.motivo_bloqueio ?? null}
                        onConfirmar={podeConfirmar && cartao
                          ? () => confirmMutation.mutate({ messageId: cartao.message_id })
                          : undefined}
                        confirmando={confirmMutation.isPending}
                        onCartaoDeCriacao={setCartao}
                        passoInicial={passo} />
      )}
    </Card>
  )
}
